import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { FiX, FiPlus, FiExternalLink, FiSmile } from 'react-icons/fi';
import { FaVoteYea } from 'react-icons/fa';
import NewsFeed from './NewsFeed';
import CustomEmojiPicker from './Common/EmojiPicker';
import { ENABLE_MODAL_SWIPE_BACK } from '../utils/mobileModalSwipe';
import { getCustomEmojiSizeForArea, subscribeEmojiSettings } from '../utils/emojiSettings';
import {
  GOOGLE_FONTS_WELCOME_CATALOG,
  googleFontFamilyCssValue,
  collectGoogleFontNamesFromStacks,
  syncNewsGoogleFontLinkElements,
  removeAllNewsGoogleFontPreviewLinks,
  syncNewsCustomCssLinks,
  removeAllNewsCustomCssPreviewLinks,
} from '../utils/googleFontsWelcome';
import { usePersistedNewsGoogleFonts } from '../hooks/usePersistedNewsGoogleFonts';
import {
  NEWS_CONTENT_BACKGROUND_PRESETS,
  NEWS_CARD_BACKGROUND_PRESETS,
  NEWS_POLL_CARD_BACKGROUND_PRESETS,
  getNewsContentBackgroundCss,
  getNewsCardBackgroundCss,
} from '../utils/newsContentBackgrounds';
import { extractFirstCssTextColorFromHtml } from '../utils/newsContentColorMeta';

/** Основной слой модалки «Новости»; вложенные формы должны быть выше. */
const Z_NEWS_MOBILE = 210000;
const Z_NEWS_MOBILE_NESTED = 220000;
const Z_NEWS_GOOGLE_FONT_PICKER = 230000;

/** Пустой ли HTML тела новости (без видимого текста). */
function newsRichContentIsEmpty(html) {
  if (html == null || !String(html).trim()) return true;
  const d = document.createElement('div');
  d.innerHTML = html;
  const t = (d.textContent || '').replace(/\u00a0/g, ' ').trim();
  return !t;
}

const nestedModalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: Z_NEWS_MOBILE_NESTED,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'stretch',
  padding: 0,
  margin: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.88)',
  boxSizing: 'border-box',
};

/** Полноэкранная белая панель под мобильный safe-area */
const nestedModalPanelStyle = {
  flex: 1,
  width: '100%',
  minHeight: 0,
  maxWidth: 'none',
  backgroundColor: '#fff',
  borderRadius: 0,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxSizing: 'border-box',
  paddingTop: 'max(16px, env(safe-area-inset-top, 0px))',
  paddingBottom: 'max(20px, env(safe-area-inset-bottom, 0px))',
  paddingLeft: 'max(16px, env(safe-area-inset-left, 0px))',
  paddingRight: 'max(16px, env(safe-area-inset-right, 0px))',
};

const nestedModalScrollStyle = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  display: 'flex',
  flexDirection: 'column',
};

export default function NewsMobile({
  open,
  onClose,
  onOpenMobileSidebar,
  initialOpenAddModal = false,
  initialOpenPollModal = false,
  onConsumeInitialOpen,
}) {
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const modalRef = useRef(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPollModal, setShowPollModal] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [isPollSending, setIsPollSending] = useState(false);
  const [pollCardBackgroundId, setPollCardBackgroundId] = useState('');
  const [pollCardImageUrlDesktop, setPollCardImageUrlDesktop] = useState('');
  const [pollCardImageUrlMobile, setPollCardImageUrlMobile] = useState('');
  const [pollCardImageUploadingDesktop, setPollCardImageUploadingDesktop] = useState(false);
  const [pollCardImageUploadingMobile, setPollCardImageUploadingMobile] = useState(false);
  const pollCardImageInputRefDesktop = useRef(null);
  const pollCardImageInputRefMobile = useRef(null);
  const [tab, setTab] = useState('news');

  const {
    families: newsAddGoogleFontFamilies,
    load: loadNewsEditorGoogleFonts,
    addFamily: persistNewsGoogleFontFamily,
    removeFamily: unpersistNewsGoogleFontFamily,
  } = usePersistedNewsGoogleFonts();
  const [newsTitleFontFamily, setNewsTitleFontFamily] = useState('Arial, sans-serif');
  const [newsContentFontFamily, setNewsContentFontFamily] = useState('Arial, sans-serif');
  const [newsCustomFontUrl, setNewsCustomFontUrl] = useState('');
  const [newsCustomFontName, setNewsCustomFontName] = useState('');
  const [newsCustomFontDataUrl, setNewsCustomFontDataUrl] = useState('');
  const [googleFontsPickerOpen, setGoogleFontsPickerOpen] = useState(false);
  const [googleFontsSearch, setGoogleFontsSearch] = useState('');
  const [manualGoogleFontName, setManualGoogleFontName] = useState('');
  const googleFontsPickerApplyToRef = useRef('title');
  const addModalWasOpen = useRef(false);

  const [newsTitleColor, setNewsTitleColor] = useState('');
  const [newsContentColor, setNewsContentColor] = useState('');
  const [newsTitleFontSize, setNewsTitleFontSize] = useState('');
  const [newsContentFontSize, setNewsContentFontSize] = useState('');
  const [newsTitleFontWeight, setNewsTitleFontWeight] = useState('');
  const [newsContentFontWeight, setNewsContentFontWeight] = useState('');
  const [newsContentBackgroundId, setNewsContentBackgroundId] = useState('');
  const [newsCardBackgroundId, setNewsCardBackgroundId] = useState('');
  const [newsCardImageUrlDesktop, setNewsCardImageUrlDesktop] = useState('');
  const [newsCardImageUrlMobile, setNewsCardImageUrlMobile] = useState('');
  const [newsCardImageUploadingDesktop, setNewsCardImageUploadingDesktop] = useState(false);
  const [newsCardImageUploadingMobile, setNewsCardImageUploadingMobile] = useState(false);
  const newsCardImageInputRefDesktop = useRef(null);
  const newsCardImageInputRefMobile = useRef(null);
  const [newsCardVideoUrlDesktop, setNewsCardVideoUrlDesktop] = useState('');
  const [newsCardVideoUrlMobile, setNewsCardVideoUrlMobile] = useState('');
  const [newsCardVideoUploadingDesktop, setNewsCardVideoUploadingDesktop] = useState(false);
  const [newsCardVideoUploadingMobile, setNewsCardVideoUploadingMobile] = useState(false);
  const newsCardVideoInputRefDesktop = useRef(null);
  const newsCardVideoInputRefMobile = useRef(null);

  const [newsContentHtml, setNewsContentHtml] = useState('');
  const [showNewsEmoji, setShowNewsEmoji] = useState(false);
  const newsEditorRef = useRef(null);
  const newsEditorSavedRangeRef = useRef(null);
  const [newsCustomEmojiMap, setNewsCustomEmojiMap] = useState({});
  const [newsEmojiSizes, setNewsEmojiSizes] = useState({
    news: getCustomEmojiSizeForArea('news'),
  });

  const captureNewsEditorRange = useCallback(() => {
    const el = newsEditorRef.current;
    if (!el || typeof window === 'undefined') return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    try {
      const r = sel.getRangeAt(0);
      if (el.contains(r.commonAncestorContainer)) {
        newsEditorSavedRangeRef.current = r.cloneRange();
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  const insertNewsCustomEmojiAtCursor = useCallback(
    (token) => {
      const el = newsEditorRef.current;
      if (!el) return;
      el.focus();
      const saved = newsEditorSavedRangeRef.current;
      if (saved && el.contains(saved.commonAncestorContainer)) {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(saved);
      }
      const url = newsCustomEmojiMap[token];
      if (!url) {
        document.execCommand('insertText', false, token);
        setNewsContentHtml(el.innerHTML);
        return;
      }
      const img = document.createElement('img');
      img.src = url;
      img.alt = token;
      img.setAttribute('data-custom-emoji', 'true');
      img.setAttribute('data-token', token);
      const px = Number(newsEmojiSizes.news || 24);
      img.style.width = `${px}px`;
      img.style.height = `${px}px`;
      img.style.objectFit = 'cover';
      img.style.verticalAlign = 'middle';
      img.style.margin = '0 2px';
      img.style.borderRadius = '6px';
      const range =
        window.getSelection && window.getSelection().rangeCount > 0
          ? window.getSelection().getRangeAt(0)
          : null;
      if (range && el.contains(range.commonAncestorContainer)) {
        range.deleteContents();
        range.insertNode(img);
        range.setStartAfter(img);
        range.setEndAfter(img);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } else {
        el.appendChild(img);
      }
      setNewsContentHtml(el.innerHTML);
    },
    [newsCustomEmojiMap, newsEmojiSizes]
  );

  const addNewsGoogleFont = useCallback(
    (rawName) => {
      const t = String(rawName || '').trim();
      if (!t) return;
      const target = googleFontsPickerApplyToRef.current;
      const css = googleFontFamilyCssValue(t);
      if (target === 'content') setNewsContentFontFamily(css);
      else setNewsTitleFontFamily(css);
      setGoogleFontsPickerOpen(false);
      setManualGoogleFontName('');
      setGoogleFontsSearch('');
      void persistNewsGoogleFontFamily(t);
    },
    [persistNewsGoogleFontFamily]
  );

  const removeNewsGoogleFont = useCallback(
    (rawName) => {
      const t = String(rawName || '').trim();
      if (!t) return;
      const removedCss = googleFontFamilyCssValue(t);
      setNewsTitleFontFamily((f) => (f === removedCss ? 'Arial, sans-serif' : f));
      setNewsContentFontFamily((f) => (f === removedCss ? 'Arial, sans-serif' : f));
      void unpersistNewsGoogleFontFamily(t);
    },
    [unpersistNewsGoogleFontFamily]
  );

  const handleNewsFontFileUpload = useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      e.target.value = '';
      return;
    }
    const validExtensions = ['.ttf', '.otf', '.woff', '.woff2'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(fileExtension)) {
      alert('Неверный формат файла. Пожалуйста, выберите файл .ttf, .otf, .woff или .woff2');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => {
      alert('Ошибка при чтении файла шрифта.');
      e.target.value = '';
    };
    reader.onload = (event) => {
      try {
        const dataUrl = event.target.result;
        let fontName = file.name.replace(/\.[^.]+$/, '');
        fontName = fontName.replace(/[^a-zA-Z0-9\s\-_]/g, '').trim() || 'CustomFont';
        setNewsCustomFontUrl('');
        setNewsCustomFontName(fontName);
        setNewsCustomFontDataUrl(dataUrl);
      } catch {
        alert('Ошибка при обработке файла шрифта.');
      }
      e.target.value = '';
    };
    reader.readAsDataURL(file);
  }, []);

  useEffect(() => {
    if (showAddModal && !addModalWasOpen.current) {
      void loadNewsEditorGoogleFonts();
      setNewsTitleFontFamily('Arial, sans-serif');
      setNewsContentFontFamily('Arial, sans-serif');
      setNewsCustomFontUrl('');
      setNewsCustomFontName('');
      setNewsCustomFontDataUrl('');
      setGoogleFontsPickerOpen(false);
      setGoogleFontsSearch('');
      setManualGoogleFontName('');
      setNewsTitleColor('');
      setNewsContentColor('');
      setNewsTitleFontSize('');
      setNewsContentFontSize('');
      setNewsTitleFontWeight('');
      setNewsContentFontWeight('');
      setNewsContentBackgroundId('');
      setNewsCardBackgroundId('');
      setNewsCardImageUrlDesktop('');
      setNewsCardImageUrlMobile('');
      setNewsCardImageUploadingDesktop(false);
      setNewsCardImageUploadingMobile(false);
      if (newsCardImageInputRefDesktop.current) newsCardImageInputRefDesktop.current.value = '';
      if (newsCardImageInputRefMobile.current) newsCardImageInputRefMobile.current.value = '';
      setNewsCardVideoUrlDesktop('');
      setNewsCardVideoUrlMobile('');
      setNewsCardVideoUploadingDesktop(false);
      setNewsCardVideoUploadingMobile(false);
      if (newsCardVideoInputRefDesktop.current) newsCardVideoInputRefDesktop.current.value = '';
      if (newsCardVideoInputRefMobile.current) newsCardVideoInputRefMobile.current.value = '';
      setNewsContentHtml('');
      setShowNewsEmoji(false);
      newsEditorSavedRangeRef.current = null;
      queueMicrotask(() => {
        if (newsEditorRef.current) newsEditorRef.current.innerHTML = '';
      });
    }
    addModalWasOpen.current = showAddModal;
  }, [showAddModal, loadNewsEditorGoogleFonts]);

  useEffect(() => {
    fetch('/api/emojis/list')
      .then((r) => r.json())
      .then((list) => {
        const map = {};
        (Array.isArray(list) ? list : []).forEach((e) => {
          map[`custom:${e.name}`] = e.url;
        });
        setNewsCustomEmojiMap(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const unsub = subscribeEmojiSettings(() => {
      setNewsEmojiSizes({ news: getCustomEmojiSizeForArea('news') });
    });
    return unsub;
  }, []);

  useEffect(() => {
    const el = newsEditorRef.current;
    if (!el || typeof newsContentHtml !== 'string') return;
    if (el.innerHTML !== newsContentHtml) {
      el.innerHTML = newsContentHtml;
    }
  }, [newsContentHtml]);

  useEffect(() => {
    if (!showAddModal) return;
    syncNewsGoogleFontLinkElements(newsAddGoogleFontFamilies, { preview: true });
    return () => removeAllNewsGoogleFontPreviewLinks();
  }, [showAddModal, newsAddGoogleFontFamilies]);

  useEffect(() => {
    if (!showAddModal) return;
    const u = newsCustomFontUrl.trim();
    syncNewsCustomCssLinks(u ? [u] : [], { preview: true });
    return () => removeAllNewsCustomCssPreviewLinks();
  }, [showAddModal, newsCustomFontUrl]);

  useEffect(() => {
    if (!showAddModal) {
      document.getElementById('news-add-modal-local-font-style')?.remove();
      return;
    }
    document.getElementById('news-add-modal-local-font-style')?.remove();
    if (!newsCustomFontDataUrl || !newsCustomFontName) return;
    const style = document.createElement('style');
    style.id = 'news-add-modal-local-font-style';
    const n = String(newsCustomFontName).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
    style.textContent = `@font-face{font-family:'${n}';src:url(${JSON.stringify(newsCustomFontDataUrl)});font-display:swap;}`;
    document.head.appendChild(style);
    return () => document.getElementById('news-add-modal-local-font-style')?.remove();
  }, [showAddModal, newsCustomFontDataUrl, newsCustomFontName]);

  useEffect(() => {
    if (open && (initialOpenAddModal || initialOpenPollModal)) {
      if (initialOpenAddModal) setShowAddModal(true);
      if (initialOpenPollModal) setShowPollModal(true);
      onConsumeInitialOpen?.();
    }
  }, [open, initialOpenAddModal, initialOpenPollModal, onConsumeInitialOpen]);
  
  // Проверяем права пользователя на создание новостей
  const userRaw = localStorage.getItem('user');
  let userRole = '';
  try {
    userRole = JSON.parse(userRaw)?.role || '';
  } catch {}
  const normalizedRole = String(userRole || '').trim().toLowerCase();
  const canManageNews = ['admin', 'hr', 'hr manager', 'hr_manager', 'human resources', 'human_resources'].includes(normalizedRole);

  // Обработчики свайпа
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!ENABLE_MODAL_SWIPE_BACK) {
      touchStartX.current = null;
      touchEndX.current = null;
      return;
    }
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      onClose();
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const handleClose = () => {
    // Просто закрываем модалку, возвращаемся к SidebarNav (который остается открытым)
    onClose();
  };

  const token = localStorage.getItem('token');

  const handleNewsCardImageFile = useCallback(
    async (e, variant) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !String(file.type || '').startsWith('image/')) {
        alert('Выберите файл изображения');
        return;
      }
      if (!token) {
        alert('Нужна авторизация');
        return;
      }
      const setBusy =
        variant === 'mobile' ? setNewsCardImageUploadingMobile : setNewsCardImageUploadingDesktop;
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append('image', file);
        const res = await fetch('/api/news/card-image', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Ошибка загрузки');
        if (data.url) {
          if (variant === 'mobile') {
            setNewsCardImageUrlMobile(data.url);
            setNewsCardVideoUrlMobile('');
          } else {
            setNewsCardImageUrlDesktop(data.url);
            setNewsCardVideoUrlDesktop('');
          }
        }
      } catch (err) {
        alert(err.message || 'Не удалось загрузить изображение');
      } finally {
        setBusy(false);
      }
    },
    [token]
  );

  const resetPollCreateForm = useCallback(() => {
    setPollQuestion('');
    setPollOptions(['', '']);
    setPollCardBackgroundId('');
    setPollCardImageUrlDesktop('');
    setPollCardImageUrlMobile('');
    if (pollCardImageInputRefDesktop.current) pollCardImageInputRefDesktop.current.value = '';
    if (pollCardImageInputRefMobile.current) pollCardImageInputRefMobile.current.value = '';
  }, []);

  const handlePollCardImageFile = useCallback(
    async (e, variant) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !String(file.type || '').startsWith('image/')) {
        alert('Выберите файл изображения');
        return;
      }
      if (!token) {
        alert('Нужна авторизация');
        return;
      }
      const setBusy =
        variant === 'mobile' ? setPollCardImageUploadingMobile : setPollCardImageUploadingDesktop;
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append('image', file);
        const res = await fetch('/api/news/card-image', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Ошибка загрузки');
        if (data.url) {
          if (variant === 'mobile') setPollCardImageUrlMobile(data.url);
          else setPollCardImageUrlDesktop(data.url);
        }
      } catch (err) {
        alert(err.message || 'Не удалось загрузить изображение');
      } finally {
        setBusy(false);
      }
    },
    [token]
  );

  const handleNewsCardVideoFile = useCallback(
    async (e, variant) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      const okMp4 =
        String(file?.type || '').toLowerCase().includes('mp4') ||
        /\.mp4$/i.test(file?.name || '');
      if (!file || !okMp4) {
        alert('Выберите видео в формате MP4');
        return;
      }
      if (!token) {
        alert('Нужна авторизация');
        return;
      }
      const setBusy =
        variant === 'mobile' ? setNewsCardVideoUploadingMobile : setNewsCardVideoUploadingDesktop;
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append('video', file);
        const res = await fetch('/api/news/card-video', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Ошибка загрузки');
        if (data.url) {
          if (variant === 'mobile') {
            setNewsCardVideoUrlMobile(data.url);
            setNewsCardImageUrlMobile('');
          } else {
            setNewsCardVideoUrlDesktop(data.url);
            setNewsCardImageUrlDesktop('');
          }
        }
      } catch (err) {
        alert(err.message || 'Не удалось загрузить видео');
      } finally {
        setBusy(false);
      }
    },
    [token]
  );

  const newsLocalFontOptionValue =
    newsCustomFontName && newsCustomFontDataUrl ? `'${newsCustomFontName}', Arial, sans-serif` : '';

  const newsFontSelectStyle = {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    background: '#fff',
    color: '#111',
    fontSize: '16px',
    marginBottom: '8px',
    boxSizing: 'border-box',
  };

  const titlePreviewColor = newsTitleColor.trim() || '#0f172a';
  const contentPreviewColor = newsContentColor.trim() || '#1e293b';
  const newsEditorSurfaceBackground =
    getNewsContentBackgroundCss(newsContentBackgroundId) || 'rgba(255,255,255,0.92)';
  const titlePreviewSize = newsTitleFontSize.trim() || '1.06em';
  const contentPreviewSize = newsContentFontSize.trim() || '1.06rem';
  const titlePreviewWeight = newsTitleFontWeight.trim() || '700';
  const contentPreviewWeight = newsContentFontWeight.trim() || '400';

  const newsStyleRowStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'center',
    marginBottom: '10px',
  };

  const newsStyleLabelStyle = { fontSize: '0.8em', color: '#64748b', minWidth: '72px' };

  return (
    <>
      {open && ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        zIndex: Z_NEWS_MOBILE, // Выше app-modal-root (200000), если открыто поверх других слоёв
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
        backdropFilter: 'blur(8px)',
      }}
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          background: 'radial-gradient(120% 80% at 50% -20%, rgba(56, 189, 248, 0.12) 0%, transparent 55%), linear-gradient(180deg, #0f1419 0%, #1a1f26 45%, #12161c 100%)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
        onClick={(e) => e.stopPropagation()}
        {...(ENABLE_MODAL_SWIPE_BACK
          ? { onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd }
          : {})}
      >
        {/* Шапка в потоке (не fixed внутри scroll): корректный safe-area и клики на iOS/Android */}
        <div
          style={{
            flexShrink: 0,
            paddingTop: 'max(10px, env(safe-area-inset-top, 0px))',
            paddingLeft: '12px',
            paddingRight: '12px',
            paddingBottom: '10px',
            background: 'rgba(15, 20, 25, 0.92)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            boxShadow: '0 1px 0 rgba(255, 255, 255, 0.06)',
            position: 'relative',
            zIndex: 5,
          }}
        >
          <div
            style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
              gap: '8px',
              minHeight: '44px',
          }}
        >
          <button
              type="button"
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#fff',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'background-color 0.2s',
                flexShrink: 0,
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            title="Назад"
          >
            <FaArrowLeft />
          </button>
          
          <h2
            style={{
              color: '#fff',
                fontSize: '17px',
              fontWeight: 700,
              margin: 0,
              flex: 1,
              textAlign: 'center',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                padding: '0 4px',
            }}
          >
            Новости
          </h2>

            <button
              type="button"
              onClick={handleClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#fff',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                transition: 'background-color 0.2s',
                flexShrink: 0,
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title="Закрыть"
            >
              <FiX />
            </button>
          </div>

            {canManageNews && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginTop: '10px',
                rowGap: '10px',
              }}
            >
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowAddModal(true);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #2193b0 0%, #43e97b 100%)',
                    border: 'none',
                    color: '#fff',
                  fontSize: '13px',
                    cursor: 'pointer',
                  padding: '10px 14px',
                  minHeight: '44px',
                  display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  borderRadius: '10px',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(67, 233, 123, 0.3)',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  }}
                  title="Добавить новость"
                >
                <FiPlus size={16} style={{ marginRight: '6px' }} />
                  Новость
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowPollModal(true);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                    border: 'none',
                    color: '#fff',
                  fontSize: '13px',
                    cursor: 'pointer',
                  padding: '10px 14px',
                  minHeight: '44px',
                  display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  borderRadius: '10px',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  }}
                  title="Создать голосование"
                >
                <FaVoteYea size={16} style={{ marginRight: '6px' }} />
                  Голосование
                </button>
                <button
                type="button"
                  onClick={(e) => {
                  e.preventDefault();
                    e.stopPropagation();
                    setTab('news');
                  }}
                  style={{
                    background: tab === 'news' ? 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' : 'rgba(255, 255, 255, 0.1)',
                    border: tab === 'news' ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                    color: '#fff',
                  fontSize: '13px',
                    cursor: 'pointer',
                  padding: '10px 14px',
                  minHeight: '44px',
                  display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  borderRadius: '10px',
                    transition: 'all 0.2s',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  }}
                  title="Новости"
                >
                  Новости
                </button>
            </div>
          )}
        </div>

        {/* Контент — только эта область скроллится */}
        <div
          style={{
            flex: 1,
            width: '100%',
            maxWidth: '100%',
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            paddingLeft: '12px',
            paddingRight: '12px',
            paddingTop: '8px',
            paddingBottom: 'max(24px, env(safe-area-inset-bottom, 0px))',
            boxSizing: 'border-box',
          }}
        >
          <NewsFeed modal onClose={handleClose} token={token} />
        </div>

        {ENABLE_MODAL_SWIPE_BACK && (
        <div
          style={{
            position: 'fixed',
              bottom: 'max(12px, env(safe-area-inset-bottom, 12px))',
            left: '50%',
            transform: 'translateX(-50%)',
              color: 'rgba(148, 163, 184, 0.55)',
              fontSize: '11px',
              letterSpacing: '0.02em',
            pointerEvents: 'none',
            zIndex: 10002,
              padding: '6px 12px',
              borderRadius: 999,
              background: 'rgba(15, 23, 42, 0.5)',
              backdropFilter: 'blur(8px)',
          }}
        >
            ← Свайп влево — назад
        </div>
        )}
      </div>
    </div>,
    document.body
      )}
      
      {/* Модалка создания новости */}
      {showAddModal &&
        ReactDOM.createPortal(
        <div style={nestedModalOverlayStyle} onClick={() => setShowAddModal(false)}>
          <div style={nestedModalPanelStyle} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
                flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                marginBottom: '16px',
              }}
            >
              <h3 style={{ margin: 0, color: '#e74c3c', fontSize: '1.2em', fontWeight: 700 }}>
                Добавить новость
              </h3>
            <button
                type="button"
              onClick={() => setShowAddModal(false)}
              style={{
                  fontSize: '28px',
                  lineHeight: 1,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#2193b0',
                  padding: '8px',
                  flexShrink: 0,
                  touchAction: 'manipulation',
              }}
                aria-label="Закрыть"
            >
              ×
            </button>
            </div>
            <div style={nestedModalScrollStyle}>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const title = formData.get('title');
                  const content = newsEditorRef.current?.innerHTML ?? newsContentHtml ?? '';
                  const titleTrim = title && String(title).trim();
                  const hasCardVideo = Boolean(
                    newsCardVideoUrlDesktop.trim() || newsCardVideoUrlMobile.trim()
                  );
                  const bodyEmpty = newsRichContentIsEmpty(content);
                  if (!hasCardVideo && (!titleTrim || bodyEmpty)) {
                    alert('Укажите заголовок и текст новости или загрузите видео для карточки (MP4).');
                    return;
                  }

                  try {
                    const localNameTrim =
                      newsCustomFontName && newsCustomFontDataUrl
                        ? String(newsCustomFontName).trim()
                        : '';
                    const fromStacks = collectGoogleFontNamesFromStacks(
                      newsTitleFontFamily,
                      newsContentFontFamily
                    ).filter((n) => n && n !== localNameTrim);
                    const mergedGoogleFamilies = [
                      ...new Set([...newsAddGoogleFontFamilies, ...fromStacks]),
                    ];

                    const payload = {
                      title: titleTrim || '',
                      content: bodyEmpty ? '' : content,
                      titleFontFamily:
                        newsTitleFontFamily && newsTitleFontFamily !== 'Arial, sans-serif'
                          ? newsTitleFontFamily
                          : undefined,
                      contentFontFamily:
                        newsContentFontFamily && newsContentFontFamily !== 'Arial, sans-serif'
                          ? newsContentFontFamily
                          : undefined,
                      newsGoogleFontFamilies:
                        mergedGoogleFamilies.length > 0 ? mergedGoogleFamilies : undefined,
                      newsCustomFontCssUrl: newsCustomFontUrl.trim() || undefined,
                      newsLocalFontsJson:
                        newsCustomFontName && newsCustomFontDataUrl
                          ? JSON.stringify({
                              shared: { name: newsCustomFontName, dataUrl: newsCustomFontDataUrl },
                            })
                          : undefined,
                      titleColor: newsTitleColor.trim() || undefined,
                      contentColor:
                        newsContentColor.trim() ||
                        extractFirstCssTextColorFromHtml(content) ||
                        undefined,
                      titleFontSize: newsTitleFontSize.trim() || undefined,
                      contentFontSize: newsContentFontSize.trim() || undefined,
                      titleFontWeight: newsTitleFontWeight.trim() || undefined,
                      contentFontWeight: newsContentFontWeight.trim() || undefined,
                      contentBackground: newsContentBackgroundId.trim() || undefined,
                      cardBackground: newsCardBackgroundId.trim() || undefined,
                      cardImageUrl: newsCardImageUrlDesktop.trim() || undefined,
                      cardImageUrlMobile: newsCardImageUrlMobile.trim() || undefined,
                      cardVideoUrl: newsCardVideoUrlDesktop.trim() || undefined,
                      cardVideoUrlMobile: newsCardVideoUrlMobile.trim() || undefined,
                    };
                  const response = await fetch('/api/news', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`,
                    },
                      body: JSON.stringify(payload),
                  });
                  
                  if (response.ok) {
                      setNewsContentHtml('');
                      setShowNewsEmoji(false);
                      if (newsEditorRef.current) newsEditorRef.current.innerHTML = '';
                    setShowAddModal(false);
                    window.dispatchEvent(new CustomEvent('news-published'));
                  }
                } catch (error) {
                  console.error('Ошибка создания новости:', error);
                  alert('Ошибка создания новости');
                }
              }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                  flex: 1,
                  minHeight: 'min-content',
                }}
            >
              <input
                name="title"
                  placeholder="Заголовок (необязательно, если есть видео карточки MP4)"
                style={{
                    padding: '12px 14px',
                  borderRadius: '10px',
                  border: '1.5px solid #2193b0',
                    fontWeight: titlePreviewWeight,
                    fontSize: titlePreviewSize,
                    color: titlePreviewColor,
                  background: 'rgba(255,255,255,0.92)',
                    width: '100%',
                    boxSizing: 'border-box',
                    fontFamily: newsTitleFontFamily,
                  }}
                />
                <div style={{ position: 'relative', width: '100%' }}>
                  <div
                    ref={newsEditorRef}
                    contentEditable
                    dir="ltr"
                    suppressContentEditableWarning
                    aria-label="Текст новости"
                    onInput={(ev) => setNewsContentHtml(ev.currentTarget.innerHTML)}
                style={{
                      padding: '12px 14px',
                      paddingRight: 52,
                  borderRadius: '10px',
                  border: '1.5px solid #e74c3c',
                      fontSize: contentPreviewSize,
                      fontWeight: contentPreviewWeight,
                      color: contentPreviewColor,
                      background: newsEditorSurfaceBackground,
                      minHeight: '200px',
                      width: '100%',
                      boxSizing: 'border-box',
                      fontFamily: newsContentFontFamily,
                      outline: 'none',
                      overflowY: 'auto',
                      textAlign: 'left',
                      WebkitUserSelect: 'text',
                      userSelect: 'text',
                    }}
                  />
                  {newsRichContentIsEmpty(newsContentHtml) && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 14,
                        top: 12,
                        right: 52,
                        color: '#94a3b8',
                        fontSize: contentPreviewSize,
                        pointerEvents: 'none',
                        userSelect: 'none',
                        lineHeight: 1.45,
                      }}
                    >
                      Текст новости
                    </div>
                  )}
                  <button
                    type="button"
                    title="Эмодзи"
                    onPointerDown={captureNewsEditorRange}
                    onClick={() => setShowNewsEmoji((v) => !v)}
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      background: 'linear-gradient(135deg, #ffe082 0%, #fcb69f 100%)',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 10px',
                      cursor: 'pointer',
                      boxShadow: '0 1px 6px #e74c3c33',
                      color: '#232931',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      touchAction: 'manipulation',
                    }}
                  >
                    <FiSmile size={20} />
                  </button>
                  {showNewsEmoji && (
                    <CustomEmojiPicker
                      isOpen={showNewsEmoji}
                      onClose={() => setShowNewsEmoji(false)}
                      stackZIndexBase={Z_NEWS_GOOGLE_FONT_PICKER}
                      onEmojiSelect={(emoji) => {
                        const str = String(emoji || '');
                        if (str.startsWith('custom:')) {
                          insertNewsCustomEmojiAtCursor(str);
                          return;
                        }
                        const el = newsEditorRef.current;
                        if (!el) return;
                        el.focus();
                        const saved = newsEditorSavedRangeRef.current;
                        if (saved && el.contains(saved.commonAncestorContainer)) {
                          const sel = window.getSelection();
                          sel.removeAllRanges();
                          sel.addRange(saved);
                        }
                        document.execCommand('insertText', false, str);
                        setNewsContentHtml(el.innerHTML);
                      }}
                    />
                  )}
                </div>

                <div
                  style={{
                    padding: '14px',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    background: '#f8fafc',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{
                      paddingBottom: 12,
                      marginBottom: 12,
                      borderBottom: '1px dashed #cbd5e1',
                    }}
                  >
                    <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.95em' }}>
                      Фон карточки в ленте
                    </div>
                    <div style={{ fontSize: '0.78em', color: '#64748b', marginTop: 4, lineHeight: 1.45, marginBottom: 10 }}>
                      Вся карточка новости в модалке. Это не фон области текста — он настраивается ниже в блоке про текст.
                    </div>
                    <div style={{ ...newsStyleRowStyle, alignItems: 'center', marginBottom: 0 }}>
                      <span style={newsStyleLabelStyle}>Карточка</span>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          flex: 1,
                          minWidth: 0,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span
                          title="Образец фона карточки"
                          style={{
                            width: 40,
                            height: 32,
                            borderRadius: 8,
                            border: '1px solid #cbd5e1',
                            flexShrink: 0,
                            background: newsCardBackgroundId
                              ? getNewsCardBackgroundCss(newsCardBackgroundId)
                              : 'repeating-conic-gradient(#f1f5f9 0% 25%, #e2e8f0 0% 50%) 50% / 12px 12px',
                          }}
                        />
                        <select
                          value={newsCardBackgroundId}
                          onChange={(e) => setNewsCardBackgroundId(e.target.value)}
                          aria-label="Фон карточки новости в ленте"
                          style={{
                            flex: '0 1 auto',
                            maxWidth: 280,
                            width: 'auto',
                            padding: '10px',
                            borderRadius: 8,
                            border: '1px solid #cbd5e1',
                            fontSize: 15,
                            boxSizing: 'border-box',
                          }}
                        >
                          <option value="">По умолчанию (как в приложении)</option>
                          <optgroup label="Светлые карточки">
                            {NEWS_CARD_BACKGROUND_PRESETS.filter((p) => p.group === 'light').map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.label}
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="Тёмные карточки">
                            {NEWS_CARD_BACKGROUND_PRESETS.filter((p) => p.group === 'dark').map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.label}
                              </option>
                            ))}
                          </optgroup>
                        </select>
                      </div>
                    </div>
                    <div
                      style={{
                        marginTop: 12,
                        paddingTop: 12,
                        borderTop: '1px dashed #cbd5e1',
                      }}
                    >
                      <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9em', marginBottom: 8 }}>
                        Фото фона карточки (два варианта)
                      </div>
                      <div
                        style={{
                          fontSize: '0.78em',
                          color: '#64748b',
                          marginBottom: 12,
                          lineHeight: 1.45,
                        }}
                      >
                        На телефоне и на ПК можно показать разные картинки (вертикальная и широкая). Если загрузить только один вариант, он будет и там и там. JPEG, PNG, GIF, WebP, до 8 МБ каждая. Фото и видео для одного варианта экрана взаимоисключающие.
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.85em', marginBottom: 6 }}>
                          Для телефона (узкий экран)
                        </div>
                        <input
                          ref={newsCardImageInputRefMobile}
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          disabled={newsCardImageUploadingMobile}
                          onChange={(ev) => handleNewsCardImageFile(ev, 'mobile')}
                          style={{ width: '100%', fontSize: '0.9em' }}
                        />
                        {newsCardImageUploadingMobile ? (
                          <div style={{ marginTop: 6, fontSize: '0.85em', color: '#64748b' }}>Загрузка…</div>
                        ) : null}
                        {newsCardImageUrlMobile ? (
                          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <img
                              src={newsCardImageUrlMobile}
                              alt=""
                              style={{
                                width: '100%',
                                maxHeight: 120,
                                objectFit: 'cover',
                                borderRadius: 10,
                                border: '1px solid #e2e8f0',
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setNewsCardImageUrlMobile('');
                                if (newsCardImageInputRefMobile.current) newsCardImageInputRefMobile.current.value = '';
                              }}
                              style={{
                                alignSelf: 'flex-start',
                                border: 'none',
                                background: 'transparent',
                                color: '#dc2626',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                fontSize: '0.9em',
                              }}
                            >
                              Убрать (телефон)
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.85em', marginBottom: 6 }}>
                          Для ПК / широкого экрана
                        </div>
                        <input
                          ref={newsCardImageInputRefDesktop}
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          disabled={newsCardImageUploadingDesktop}
                          onChange={(ev) => handleNewsCardImageFile(ev, 'desktop')}
                          style={{ width: '100%', fontSize: '0.9em' }}
                        />
                        {newsCardImageUploadingDesktop ? (
                          <div style={{ marginTop: 6, fontSize: '0.85em', color: '#64748b' }}>Загрузка…</div>
                        ) : null}
                        {newsCardImageUrlDesktop ? (
                          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <img
                              src={newsCardImageUrlDesktop}
                              alt=""
                              style={{
                                width: '100%',
                                maxHeight: 120,
                                objectFit: 'cover',
                                borderRadius: 10,
                                border: '1px solid #e2e8f0',
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setNewsCardImageUrlDesktop('');
                                if (newsCardImageInputRefDesktop.current) newsCardImageInputRefDesktop.current.value = '';
                              }}
                              style={{
                                alignSelf: 'flex-start',
                                border: 'none',
                                background: 'transparent',
                                color: '#dc2626',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                fontSize: '0.9em',
                              }}
                            >
                              Убрать (ПК)
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px dashed #cbd5e1' }}>
                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9em', marginBottom: 8 }}>
                          Видео фона карточки (MP4)
                        </div>
                        <div
                          style={{
                            fontSize: '0.78em',
                            color: '#64748b',
                            marginBottom: 12,
                            lineHeight: 1.45,
                          }}
                        >
                          Два варианта — телефон и ПК, до 100 МБ. С только видео заголовок и текст не обязательны; без текста затемнение на ролике не показывается.
                        </div>
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.85em', marginBottom: 6 }}>
                            Видео для телефона
                          </div>
                          <input
                            ref={newsCardVideoInputRefMobile}
                            type="file"
                            accept="video/mp4,.mp4"
                            disabled={newsCardVideoUploadingMobile}
                            onChange={(ev) => handleNewsCardVideoFile(ev, 'mobile')}
                            style={{ width: '100%', fontSize: '0.9em' }}
                          />
                          {newsCardVideoUploadingMobile ? (
                            <div style={{ marginTop: 6, fontSize: '0.85em', color: '#64748b' }}>Загрузка…</div>
                          ) : null}
                          {newsCardVideoUrlMobile ? (
                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <video
                                src={newsCardVideoUrlMobile}
                                muted
                                playsInline
                                controls
                                style={{
                                  width: '100%',
                                  maxHeight: 120,
                                  objectFit: 'cover',
                                  borderRadius: 10,
                                  border: '1px solid #e2e8f0',
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setNewsCardVideoUrlMobile('');
                                  if (newsCardVideoInputRefMobile.current) newsCardVideoInputRefMobile.current.value = '';
                                }}
                                style={{
                                  alignSelf: 'flex-start',
                                  border: 'none',
                                  background: 'transparent',
                                  color: '#dc2626',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  fontSize: '0.9em',
                                }}
                              >
                                Убрать видео (телефон)
                              </button>
                            </div>
                          ) : null}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.85em', marginBottom: 6 }}>
                            Видео для ПК / широкого экрана
                          </div>
                          <input
                            ref={newsCardVideoInputRefDesktop}
                            type="file"
                            accept="video/mp4,.mp4"
                            disabled={newsCardVideoUploadingDesktop}
                            onChange={(ev) => handleNewsCardVideoFile(ev, 'desktop')}
                            style={{ width: '100%', fontSize: '0.9em' }}
                          />
                          {newsCardVideoUploadingDesktop ? (
                            <div style={{ marginTop: 6, fontSize: '0.85em', color: '#64748b' }}>Загрузка…</div>
                          ) : null}
                          {newsCardVideoUrlDesktop ? (
                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <video
                                src={newsCardVideoUrlDesktop}
                                muted
                                playsInline
                                controls
                                style={{
                                  width: '100%',
                                  maxHeight: 120,
                                  objectFit: 'cover',
                                  borderRadius: 10,
                                  border: '1px solid #e2e8f0',
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setNewsCardVideoUrlDesktop('');
                                  if (newsCardVideoInputRefDesktop.current) newsCardVideoInputRefDesktop.current.value = '';
                                }}
                                style={{
                                  alignSelf: 'flex-start',
                                  border: 'none',
                                  background: 'transparent',
                                  color: '#dc2626',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  fontSize: '0.9em',
                                }}
                              >
                                Убрать видео (ПК)
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.95em' }}>Шрифты</div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '0.9em' }}>
                      Шрифт заголовка
                    </label>
                    <select
                      value={newsTitleFontFamily}
                      onChange={(e) => setNewsTitleFontFamily(e.target.value)}
                      style={newsFontSelectStyle}
                    >
                      <option value="Arial, sans-serif">Arial</option>
                      <option value="'Times New Roman', serif">Times New Roman</option>
                      <option value="'Courier New', monospace">Courier New</option>
                      <option value="Georgia, serif">Georgia</option>
                      <option value="Verdana, sans-serif">Verdana</option>
                      <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                      <option value="Impact, sans-serif">Impact</option>
                      <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                      {newsAddGoogleFontFamilies.filter(Boolean).map((name) => (
                        <option key={`nt-${name}`} value={googleFontFamilyCssValue(name)}>
                          {name} (Google Fonts)
                        </option>
                      ))}
                      {newsLocalFontOptionValue ? (
                        <option value={newsLocalFontOptionValue}>{newsCustomFontName} (локальный)</option>
                      ) : null}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        googleFontsPickerApplyToRef.current = 'title';
                        setGoogleFontsPickerOpen(true);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        borderRadius: '10px',
                        border: '1px solid rgba(33, 147, 176, 0.45)',
                        background: 'rgba(33, 147, 176, 0.1)',
                        color: '#0e7490',
                        fontSize: '0.95em',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        touchAction: 'manipulation',
                      }}
                    >
                      <FiExternalLink size={18} />
                      Google Fonts — добавить в список (заголовок)
                    </button>
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e2e8f0' }}>
                      <span
                        style={{
                          display: 'block',
                          fontWeight: 600,
                          color: '#334155',
                          fontSize: '0.85em',
                          marginBottom: 8,
                        }}
                      >
                        Цвет, размер и жирность заголовка
                      </span>
                      <div style={newsStyleRowStyle}>
                        <span style={newsStyleLabelStyle}>Цвет</span>
                        <input
                          type="color"
                          aria-label="Цвет заголовка"
                          value={
                            /^#[0-9a-fA-F]{6}$/i.test(newsTitleColor.trim())
                              ? newsTitleColor.trim()
                              : '#0f172a'
                          }
                          onChange={(e) => setNewsTitleColor(e.target.value)}
                          style={{
                            width: 44,
                            height: 36,
                            padding: 0,
                            border: '1px solid #cbd5e1',
                            borderRadius: 8,
                            cursor: 'pointer',
                          }}
                        />
                        <input
                          type="text"
                          value={newsTitleColor}
                          onChange={(e) => setNewsTitleColor(e.target.value)}
                          placeholder="#0f172a или rgb(…)"
                          style={{
                            flex: 1,
                            minWidth: 100,
                            padding: '10px',
                            borderRadius: 8,
                            border: '1px solid #cbd5e1',
                            fontSize: 15,
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>
                      <div style={newsStyleRowStyle}>
                        <span style={newsStyleLabelStyle}>Размер</span>
                        <input
                          type="text"
                          value={newsTitleFontSize}
                          onChange={(e) => setNewsTitleFontSize(e.target.value)}
                          placeholder="напр. 1.1em, 20px"
                          style={{
                            flex: 1,
                            minWidth: 0,
                            padding: '10px',
                            borderRadius: 8,
                            border: '1px solid #cbd5e1',
                            fontSize: 15,
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>
                      <div style={{ ...newsStyleRowStyle, marginBottom: 0 }}>
                        <span style={newsStyleLabelStyle}>Жирность</span>
                        <select
                          value={newsTitleFontWeight}
                          onChange={(e) => setNewsTitleFontWeight(e.target.value)}
                          style={{ flex: 1, ...newsFontSelectStyle, marginBottom: 0 }}
                        >
                          <option value="">По умолчанию</option>
                          <option value="300">300</option>
                          <option value="400">400 — обычный</option>
                          <option value="500">500</option>
                          <option value="600">600</option>
                          <option value="700">700 — жирный</option>
                          <option value="800">800</option>
                          <option value="900">900</option>
                          <option value="normal">normal</option>
                          <option value="bold">bold</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '0.9em' }}>
                      Шрифт текста новости
                    </label>
                    <select
                      value={newsContentFontFamily}
                      onChange={(e) => setNewsContentFontFamily(e.target.value)}
                      style={newsFontSelectStyle}
                    >
                      <option value="Arial, sans-serif">Arial</option>
                      <option value="'Times New Roman', serif">Times New Roman</option>
                      <option value="'Courier New', monospace">Courier New</option>
                      <option value="Georgia, serif">Georgia</option>
                      <option value="Verdana, sans-serif">Verdana</option>
                      <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                      <option value="Impact, sans-serif">Impact</option>
                      <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                      {newsAddGoogleFontFamilies.filter(Boolean).map((name) => (
                        <option key={`nc-${name}`} value={googleFontFamilyCssValue(name)}>
                          {name} (Google Fonts)
                        </option>
                      ))}
                      {newsLocalFontOptionValue ? (
                        <option value={newsLocalFontOptionValue}>{newsCustomFontName} (локальный)</option>
                      ) : null}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        googleFontsPickerApplyToRef.current = 'content';
                        setGoogleFontsPickerOpen(true);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        borderRadius: '10px',
                        border: '1px solid rgba(231, 76, 60, 0.4)',
                        background: 'rgba(231, 76, 60, 0.08)',
                        color: '#c2410c',
                        fontSize: '0.95em',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        touchAction: 'manipulation',
                      }}
                    >
                      <FiExternalLink size={18} />
                      Google Fonts — добавить в список (текст)
                    </button>
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e2e8f0' }}>
                      <span
                        style={{
                          display: 'block',
                          fontWeight: 600,
                          color: '#334155',
                          fontSize: '0.85em',
                          marginBottom: 8,
                        }}
                      >
                        Цвет, размер и жирность текста
                      </span>
                      <div style={newsStyleRowStyle}>
                        <span style={newsStyleLabelStyle}>Цвет</span>
                        <input
                          type="color"
                          aria-label="Цвет текста новости"
                          value={
                            /^#[0-9a-fA-F]{6}$/i.test(newsContentColor.trim())
                              ? newsContentColor.trim()
                              : '#1e293b'
                          }
                          onChange={(e) => setNewsContentColor(e.target.value)}
                          style={{
                            width: 44,
                            height: 36,
                            padding: 0,
                            border: '1px solid #cbd5e1',
                            borderRadius: 8,
                            cursor: 'pointer',
                          }}
                        />
                        <input
                          type="text"
                          value={newsContentColor}
                          onChange={(e) => setNewsContentColor(e.target.value)}
                          placeholder="#1e293b или rgb(…)"
                          style={{
                            flex: 1,
                            minWidth: 100,
                            padding: '10px',
                            borderRadius: 8,
                            border: '1px solid #cbd5e1',
                            fontSize: 15,
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>
                      <div style={{ ...newsStyleRowStyle, alignItems: 'center' }}>
                        <span style={newsStyleLabelStyle}>Фон текста</span>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            flex: 1,
                            minWidth: 0,
                            flexWrap: 'wrap',
                          }}
                        >
                          <span
                            title="Образец выбранного фона"
                            style={{
                              width: 40,
                              height: 32,
                              borderRadius: 8,
                              border: '1px solid #cbd5e1',
                              flexShrink: 0,
                              background: newsContentBackgroundId
                                ? getNewsContentBackgroundCss(newsContentBackgroundId)
                                : 'repeating-conic-gradient(#f1f5f9 0% 25%, #e2e8f0 0% 50%) 50% / 12px 12px',
                            }}
                          />
                          <select
                            value={newsContentBackgroundId}
                            onChange={(e) => setNewsContentBackgroundId(e.target.value)}
                            aria-label="Фон области текста новости"
                            style={{
                              flex: '0 1 auto',
                              maxWidth: 260,
                              width: 'auto',
                              padding: '10px',
                              borderRadius: 8,
                              border: '1px solid #cbd5e1',
                              fontSize: 15,
                              boxSizing: 'border-box',
                            }}
                          >
                            <option value="">Без фона</option>
                            <optgroup label="Светлые">
                              {NEWS_CONTENT_BACKGROUND_PRESETS.filter((p) => p.group === 'light').map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.label}
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="Тёмные">
                              {NEWS_CONTENT_BACKGROUND_PRESETS.filter((p) => p.group === 'dark').map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.label}
                                </option>
                              ))}
                            </optgroup>
                          </select>
                        </div>
                      </div>
                      <div style={newsStyleRowStyle}>
                        <span style={newsStyleLabelStyle}>Размер</span>
                        <input
                          type="text"
                          value={newsContentFontSize}
                          onChange={(e) => setNewsContentFontSize(e.target.value)}
                          placeholder="напр. 1.02em, 16px"
                          style={{
                            flex: 1,
                            minWidth: 0,
                            padding: '10px',
                            borderRadius: 8,
                            border: '1px solid #cbd5e1',
                            fontSize: 15,
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>
                      <div style={{ ...newsStyleRowStyle, marginBottom: 0 }}>
                        <span style={newsStyleLabelStyle}>Жирность</span>
                        <select
                          value={newsContentFontWeight}
                          onChange={(e) => setNewsContentFontWeight(e.target.value)}
                          style={{ flex: 1, ...newsFontSelectStyle, marginBottom: 0 }}
                        >
                          <option value="">По умолчанию</option>
                          <option value="300">300</option>
                          <option value="400">400 — обычный</option>
                          <option value="500">500</option>
                          <option value="600">600</option>
                          <option value="700">700 — жирный</option>
                          <option value="800">800</option>
                          <option value="900">900</option>
                          <option value="normal">normal</option>
                          <option value="bold">bold</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {newsAddGoogleFontFamilies.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {newsAddGoogleFontFamilies.map((name) => (
                        <span
                          key={name}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '4px 10px',
                            borderRadius: '999px',
                            background: '#e2e8f0',
                            color: '#334155',
                            fontSize: '0.82em',
                          }}
                        >
                          {name}
                          <button
                            type="button"
                            onClick={() => removeNewsGoogleFont(name)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: '#dc2626',
                              cursor: 'pointer',
                              padding: 0,
                              fontSize: '1.1em',
                              lineHeight: 1,
                            }}
                            aria-label={`Убрать ${name}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div style={{ fontSize: '0.8em', color: '#64748b', lineHeight: 1.45 }}>
                    Любой шрифт с{' '}
                    <a href="https://fonts.google.com" target="_blank" rel="noopener noreferrer">
                      fonts.google.com
                    </a>{' '}
                    можно добавить вручную в каталоге ниже. Имя должно совпадать с названием семейства на сайте.
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '0.9em' }}>
                      URL CSS шрифта (опционально)
                    </label>
                    <div style={{ fontSize: '0.8em', color: '#64748b', marginBottom: '6px' }}>
                      Например: https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap
                    </div>
                    <input
                      type="text"
                      value={newsCustomFontUrl}
                      onChange={(e) => setNewsCustomFontUrl(e.target.value)}
                      placeholder="URL шрифта (CSS)"
                      style={{
                        ...newsFontSelectStyle,
                        marginBottom: 0,
                      }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85em', color: '#64748b', marginBottom: '6px' }}>
                      Локальный файл (.ttf, .otf, .woff, .woff2):
                    </div>
                    <input
                      type="file"
                      accept=".ttf,.otf,.woff,.woff2"
                      onChange={handleNewsFontFileUpload}
                      style={{
                        width: '100%',
                        fontSize: '0.95em',
                        color: '#334155',
                      }}
                    />
                    {newsCustomFontDataUrl && newsCustomFontName && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        <span style={{ color: '#16a34a', fontSize: '0.9em' }}>✓ Загружен: {newsCustomFontName}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const localVal = newsLocalFontOptionValue;
                            setNewsCustomFontDataUrl('');
                            setNewsCustomFontName('');
                            setNewsTitleFontFamily((f) => (f === localVal ? 'Arial, sans-serif' : f));
                            setNewsContentFontFamily((f) => (f === localVal ? 'Arial, sans-serif' : f));
                          }}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#dc2626',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontSize: '0.9em',
                          }}
                        >
                          Сбросить локальный шрифт
                        </button>
                      </div>
                    )}
                  </div>
                </div>

              <button
                type="submit"
                style={{
                  background: 'linear-gradient(135deg, #2193b0 0%, #43e97b 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                    padding: '14px 18px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px #43e97b22',
                    flexShrink: 0,
                    touchAction: 'manipulation',
                }}
              >
                Добавить
              </button>
            </form>
          </div>
      
            {googleFontsPickerOpen && (
        <div
                role="dialog"
                aria-modal="true"
                aria-label="Google Fonts"
          style={{
            position: 'fixed',
                  inset: 0,
                  zIndex: Z_NEWS_GOOGLE_FONT_PICKER,
                  background: 'rgba(0,0,0,0.72)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  padding: 0,
                }}
                onClick={() => setGoogleFontsPickerOpen(false)}
              >
                <div
                  style={{
                    maxHeight: '88dvh',
                    background: '#111827',
                    borderRadius: '16px 16px 0 0',
                    border: '1px solid #374151',
                    boxShadow: '0 -8px 40px rgba(0,0,0,0.45)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    style={{
                      padding: '14px 16px',
                      borderBottom: '1px solid #374151',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                    }}
                  >
                    <span style={{ fontWeight: 800, color: '#f9fafb', fontSize: '1.05em' }}>Google Fonts</span>
                    <button
                      type="button"
                      onClick={() => setGoogleFontsPickerOpen(false)}
                      style={{
                        border: 'none',
                        background: '#1f2937',
                        color: '#e5e7eb',
                        borderRadius: 8,
                        padding: '8px 14px',
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      Закрыть
                    </button>
                  </div>
                  <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => window.open('https://fonts.google.com', '_blank', 'noopener,noreferrer')}
                      style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
                        gap: 8,
                        padding: '12px',
                        borderRadius: 10,
                        border: '1px solid #4b5563',
                        background: '#1e3a5f',
                        color: '#fff',
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontSize: '0.95em',
                      }}
                    >
                      <FiExternalLink size={18} />
                      Открыть fonts.google.com
                    </button>
                    <input
                      type="search"
                      value={googleFontsSearch}
                      onChange={(e) => setGoogleFontsSearch(e.target.value)}
                      placeholder="Поиск в каталоге…"
            style={{
              width: '100%',
                        padding: '12px',
                        borderRadius: 8,
                        border: '1px solid #374151',
                        background: '#0e1420',
                        color: '#fff',
                        fontSize: '16px',
                        boxSizing: 'border-box',
                      }}
                    />
                    <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                      <input
                        type="text"
                        value={manualGoogleFontName}
                        onChange={(e) => setManualGoogleFontName(e.target.value)}
                        placeholder="Имя с сайта, например Caveat Brush"
                        style={{
                          flex: 1,
                          padding: '12px',
                          borderRadius: 8,
                          border: '1px solid #374151',
                          background: '#0e1420',
                          color: '#fff',
                          fontSize: '16px',
                          boxSizing: 'border-box',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => addNewsGoogleFont(manualGoogleFontName)}
                        style={{
                          padding: '0 16px',
                          borderRadius: 8,
                          border: 'none',
                          background: '#a3e635',
                          color: '#0f172a',
                          fontWeight: 800,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Добавить
                      </button>
                    </div>
                  </div>
                  <div
                    style={{
              overflowY: 'auto',
                      flex: 1,
                      padding: '4px 12px 16px',
                      WebkitOverflowScrolling: 'touch',
            }}
          >
                    {GOOGLE_FONTS_WELCOME_CATALOG.filter((f) =>
                      f.toLowerCase().includes(googleFontsSearch.trim().toLowerCase())
                    )
                      .slice(0, 120)
                      .map((family) => (
            <button
                          key={family}
                          type="button"
                          onClick={() => addNewsGoogleFont(family)}
              style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '12px 14px',
                            marginBottom: 4,
                            borderRadius: 8,
                            border: '1px solid #2f3440',
                            background: '#0e1420',
                            color: '#e5e7eb',
                            fontSize: '1em',
                            cursor: 'pointer',
                          }}
                        >
                          {family}
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
        )}
      
      {/* Модалка создания голосования */}
      {showPollModal &&
        ReactDOM.createPortal(
        <div
          style={nestedModalOverlayStyle}
          onClick={() => {
            setShowPollModal(false);
            resetPollCreateForm();
          }}
        >
          <div style={nestedModalPanelStyle} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                marginBottom: '16px',
              }}
            >
              <h3 style={{ margin: 0, color: '#ff9800', fontSize: '1.2em', fontWeight: 700 }}>
                Создать голосование
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowPollModal(false);
                  resetPollCreateForm();
                }}
                style={{
                  fontSize: '28px',
                  lineHeight: 1,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#ff9800',
                  padding: '8px',
                  flexShrink: 0,
                  touchAction: 'manipulation',
              }}
                aria-label="Закрыть"
            >
              ×
            </button>
            </div>
            <div style={nestedModalScrollStyle}>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!pollQuestion.trim() || pollOptions.some(opt => !opt.trim())) {
                  alert('Заполните вопрос и все варианты ответа');
                  return;
                }
                
                setIsPollSending(true);
                try {
                  const response = await fetch('/api/news/poll', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                      question: pollQuestion.trim(),
                      options: pollOptions.map(opt => opt.trim()).filter(Boolean),
                      cardBackground: pollCardBackgroundId.trim() || undefined,
                      cardImageUrl: pollCardImageUrlDesktop.trim() || undefined,
                      cardImageUrlMobile: pollCardImageUrlMobile.trim() || undefined,
                    }),
                  });
                  
                  if (response.ok) {
                    setShowPollModal(false);
                    resetPollCreateForm();
                    window.dispatchEvent(new CustomEvent('news-published'));
                  } else {
                    const error = await response.json().catch(() => ({}));
                    alert(`Ошибка создания голосования: ${error.error || 'Неизвестная ошибка'}`);
                  }
                } catch (error) {
                  console.error('Ошибка создания голосования:', error);
                  alert('Ошибка создания голосования');
                } finally {
                  setIsPollSending(false);
                }
              }}
              style={{ display: 'flex', flexDirection: 'column', gap: '14px', paddingBottom: '8px' }}
            >
              <input
                type="text"
                placeholder="Тема голосования"
                value={pollQuestion}
                onChange={(e) => setPollQuestion(e.target.value)}
                required
                maxLength={120}
                autoFocus
                style={{
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: '1.5px solid #ff9800',
                  fontWeight: 600,
                  fontSize: '1.06em',
                  background: 'rgba(255,255,255,0.92)',
                }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {pollOptions.map((opt, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder={`Вариант ${idx + 1}`}
                      value={opt}
                      onChange={(e) => setPollOptions(pollOptions.map((o, i) => i === idx ? e.target.value : o))}
                      required
                      maxLength={60}
                      style={{
                        flex: 1,
                        padding: '10px 14px',
                        borderRadius: '10px',
                        border: '1.5px solid #ff9800',
                        fontSize: '1em',
                        background: 'rgba(255,255,255,0.92)',
                      }}
                    />
                    {pollOptions.length > 2 && (
                      <button
                        type="button"
                        onClick={() => setPollOptions(pollOptions.filter((_, i) => i !== idx))}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#e74c3c',
                          fontSize: '22px',
                          cursor: 'pointer',
                          padding: '4px',
                        }}
                        title="Удалить вариант"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setPollOptions([...pollOptions, ''])}
                  style={{
                    background: 'none',
                    border: '1.5px dashed #ff9800',
                    color: '#ff9800',
                    borderRadius: '8px',
                    padding: '8px 14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.95em',
                  }}
                >
                  + Добавить вариант
                </button>
              </div>
              <div
                style={{
                  padding: 12,
                  borderRadius: 12,
                  border: '1px solid #ffe0b2',
                  background: 'rgba(255, 248, 240, 0.95)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <div style={{ fontWeight: 700, color: '#e65100', fontSize: '0.95em' }}>Фон карточки</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span
                    title="Образец градиента"
                    style={{
                      width: 36,
                      height: 26,
                      borderRadius: 8,
                      border: '1px solid #ffb74d',
                      flexShrink: 0,
                      background: pollCardBackgroundId
                        ? getNewsCardBackgroundCss(pollCardBackgroundId)
                        : 'repeating-conic-gradient(#ffe0b2 0% 25%, #ffcc80 0% 50%) 50% / 10px 10px',
                    }}
                  />
                  <select
                    value={pollCardBackgroundId}
                    onChange={(e) => setPollCardBackgroundId(e.target.value)}
                    aria-label="Градиент фона карточки опроса"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      padding: '8px 10px',
                      borderRadius: 10,
                      border: '1.5px solid #ff9800',
                      fontSize: '0.9em',
                      background: 'rgba(255,255,255,0.95)',
                    }}
                  >
                    <option value="">По умолчанию</option>
                    <optgroup label="Светлые">
                      {NEWS_POLL_CARD_BACKGROUND_PRESETS.filter((p) => p.group === 'light').map((p) => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Тёмные">
                      {NEWS_POLL_CARD_BACKGROUND_PRESETS.filter((p) => p.group === 'dark').map((p) => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                <div style={{ fontSize: '0.82em', color: '#795548', lineHeight: 1.45 }}>
                  Фото фона: отдельно для телефона и ПК. JPEG, PNG, GIF, WebP, до 8 МБ.
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#bf360c', fontSize: '0.88em', marginBottom: 6 }}>
                    Для телефона
                  </div>
                  <input
                    ref={pollCardImageInputRefMobile}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    disabled={pollCardImageUploadingMobile}
                    onChange={(ev) => handlePollCardImageFile(ev, 'mobile')}
                    style={{ width: '100%', fontSize: '0.9em' }}
                  />
                  {pollCardImageUploadingMobile ? (
                    <div style={{ marginTop: 6, fontSize: '0.85em', color: '#795548' }}>Загрузка…</div>
                  ) : null}
                  {pollCardImageUrlMobile ? (
                    <div style={{ marginTop: 8 }}>
                      <img
                        src={pollCardImageUrlMobile}
                        alt=""
                        style={{
                          width: '100%',
                          maxHeight: 100,
                          objectFit: 'cover',
                          borderRadius: 10,
                          border: '1px solid #ffe0b2',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPollCardImageUrlMobile('');
                          if (pollCardImageInputRefMobile.current) pollCardImageInputRefMobile.current.value = '';
                        }}
                        style={{
                          marginTop: 6,
                          border: 'none',
                          background: 'transparent',
                          color: '#c62828',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontSize: '0.88em',
                        }}
                      >
                        Убрать (телефон)
                      </button>
                    </div>
                  ) : null}
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#bf360c', fontSize: '0.88em', marginBottom: 6 }}>
                    Для ПК
                  </div>
                  <input
                    ref={pollCardImageInputRefDesktop}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    disabled={pollCardImageUploadingDesktop}
                    onChange={(ev) => handlePollCardImageFile(ev, 'desktop')}
                    style={{ width: '100%', fontSize: '0.9em' }}
                  />
                  {pollCardImageUploadingDesktop ? (
                    <div style={{ marginTop: 6, fontSize: '0.85em', color: '#795548' }}>Загрузка…</div>
                  ) : null}
                  {pollCardImageUrlDesktop ? (
                    <div style={{ marginTop: 8 }}>
                      <img
                        src={pollCardImageUrlDesktop}
                        alt=""
                        style={{
                          width: '100%',
                          maxHeight: 100,
                          objectFit: 'cover',
                          borderRadius: 10,
                          border: '1px solid #ffe0b2',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPollCardImageUrlDesktop('');
                          if (pollCardImageInputRefDesktop.current) pollCardImageInputRefDesktop.current.value = '';
                        }}
                        style={{
                          marginTop: 6,
                          border: 'none',
                          background: 'transparent',
                          color: '#c62828',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontSize: '0.88em',
                        }}
                      >
                        Убрать (ПК)
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
              <button
                type="submit"
                disabled={isPollSending}
                style={{
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px 18px',
                  fontWeight: 700,
                  cursor: isPollSending ? 'not-allowed' : 'pointer',
                  boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
                  opacity: isPollSending ? 0.7 : 1,
                }}
              >
                {isPollSending ? 'Создание...' : 'Создать'}
              </button>
            </form>
            </div>
          </div>
        </div>,
        document.body
        )}
    </>
  );
}

